import PageHeader from "../../components/ui/PageHeader";
import { StyledTableCell, StyledTableRow, TableWrapper } from "../../components/ui/table";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import {
  CronJob,
  CronJobType,
  CronJobValues,
  createCronJob,
  deleteCronJob,
  getCronJobTypes,
  getCronJobs,
  runCronJob,
  updateCronJob,
} from "../../service/CronJobService";
import { describeCron } from "../../utils/describeCron";
import { showSuccessToast } from "../../utils/Toast";
import CronJobDialog from "./CronJobDialog";




// Refresh while jobs run, so the last-run column updates on its own
const REFRESH_INTERVAL_MS = 10000;

const statusColor = (status?: string): "success" | "error" | "warning" | "default" =>
  status === "success" ? "success" : status === "error" ? "error" : status === "running" ? "warning" : "default";

export default function CronJobs() {
  const [t] = useTranslation("global");
  const isMobile = useMediaQuery("(max-width:600px)");
  const navigate = useNavigate();

  const [cronJobs, setCronJobs] = useState<CronJob[]>([]);
  const [jobTypes, setJobTypes] = useState<CronJobType[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<CronJob | null>(null);
  const [jobToDelete, setJobToDelete] = useState<CronJob | null>(null);

  const fetchCronJobs = useCallback(async () => {
    setCronJobs(await getCronJobs(isMobile, navigate));
  }, [isMobile, navigate]);

  useEffect(() => {
    fetchCronJobs();
    getCronJobTypes(isMobile, navigate).then(setJobTypes);
    const interval = setInterval(fetchCronJobs, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const jobTypeLabel = (key: string) => jobTypes.find((type) => type.key === key)?.label || key;

  const openCreate = () => {
    setEditingJob(null);
    setDialogOpen(true);
  };

  const openEdit = (cronJob: CronJob) => {
    setEditingJob(cronJob);
    setDialogOpen(true);
  };

  const handleSubmit = async (values: CronJobValues) => {
    const saved = editingJob
      ? await updateCronJob(editingJob.id, values, isMobile, navigate)
      : await createCronJob(values, isMobile, navigate);
    if (saved) {
      showSuccessToast(editingJob ? t("cronJobs.updated") : t("cronJobs.created"), isMobile);
      fetchCronJobs();
    }
    return saved;
  };

  const handleToggle = async (cronJob: CronJob) => {
    if (await updateCronJob(cronJob.id, { enabled: !cronJob.enabled }, isMobile, navigate)) {
      fetchCronJobs();
    }
  };

  const handleRun = async (cronJob: CronJob) => {
    if (await runCronJob(cronJob.id, isMobile, navigate)) {
      showSuccessToast(t("cronJobs.started", { name: cronJob.name }), isMobile);
      setTimeout(fetchCronJobs, 1000);
    }
  };

  const handleDelete = async () => {
    if (jobToDelete && (await deleteCronJob(jobToDelete.id, isMobile, navigate))) {
      showSuccessToast(t("cronJobs.deleted"), isMobile);
      fetchCronJobs();
    }
    setJobToDelete(null);
  };

  return (
    <Layout>
      <PageHeader title={t("cronJobs.title")} description={t("cronJobs.intro")} />
      <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%", mt: 1 }}>
        <Button variant="contained" onClick={openCreate}>
          {t("cronJobs.create")}
        </Button>
      </Box>
      <TableWrapper component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>{t("cronJobs.table.name")}</StyledTableCell>
              <StyledTableCell>{t("cronJobs.table.schedule")}</StyledTableCell>
              <StyledTableCell align="center">{t("cronJobs.table.enabled")}</StyledTableCell>
              <StyledTableCell>{t("cronJobs.table.lastRun")}</StyledTableCell>
              <StyledTableCell align="center">{t("cronJobs.table.actions")}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cronJobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {t("cronJobs.empty")}
                </TableCell>
              </TableRow>
            ) : (
              cronJobs.map((cronJob) => (
                <StyledTableRow key={cronJob.id}>
                  <TableCell>
                    <Typography fontWeight="bold">{cronJob.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {jobTypeLabel(cronJob.jobType)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontFamily: "monospace" }}>{cronJob.schedule}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {describeCron(cronJob.schedule, t) || cronJob.schedule} ({cronJob.timezone})
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Switch checked={cronJob.enabled} onChange={() => handleToggle(cronJob)} />
                  </TableCell>
                  <TableCell>
                    {cronJob.lastRunAt ? (
                      <>
                        <Tooltip title={cronJob.lastError || ""}>
                          <Chip
                            size="small"
                            color={statusColor(cronJob.lastStatus)}
                            label={t(`cronJobs.status.${cronJob.lastStatus || "unknown"}`)}
                          />
                        </Tooltip>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {new Date(cronJob.lastRunAt).toLocaleString("da-DK")}
                          {cronJob.lastDurationMs !== undefined && cronJob.lastStatus !== "running"
                            ? ` · ${(cronJob.lastDurationMs / 1000).toFixed(1)} s`
                            : ""}
                        </Typography>
                        {cronJob.lastStatus === "error" && cronJob.lastError && (
                          <Typography variant="caption" display="block" color="error">
                            {cronJob.lastError}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {t("cronJobs.neverRun")}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={t("cronJobs.runNow")}>
                      <span>
                        <IconButton onClick={() => handleRun(cronJob)} disabled={cronJob.lastStatus === "running"}>
                          <PlayArrowIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("cronJobs.edit")}>
                      <IconButton onClick={() => openEdit(cronJob)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("cronJobs.delete")}>
                      <IconButton onClick={() => setJobToDelete(cronJob)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableWrapper>

      <CronJobDialog
        open={dialogOpen}
        cronJob={editingJob}
        jobTypes={jobTypes}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />

      <Dialog open={Boolean(jobToDelete)} onClose={() => setJobToDelete(null)}>
        <DialogTitle>{t("cronJobs.deleteTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("cronJobs.deleteConfirm", { name: jobToDelete?.name || "" })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobToDelete(null)}>{t("cronJobs.cancel")}</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            {t("cronJobs.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
